import type { Verdict } from "../../studio/studioTypes"

/**
 * Studio V2's derived head verdict per Verordnung (framework) — the display rule
 * R1–R4 from the "Supplier Declaration Studio" concept (Prototype VIII /
 * Konstellations-Atlas), computed over the framework's statements.
 *
 * DISPLAY ONLY. This never gates anything: the approve gate is the
 * override-adjusted worklist (`frameworkGeprueft.ts`), server-re-derived in
 * `approve()`. The derivation here decides what the head pill SAYS (split pill,
 * "Unklar · Rückfrage offen", …) and which action the footer leads with — the
 * same separation the prototype draws between `deriveBlock` and its gate.
 *
 * The cascade is fixed and ordered (first hit wins):
 *   R1  the article scope is unresolvable (or a Rückfrage is already out)
 *   R2  a unit WE PURCHASE FROM says non_compliant
 *   R3  a unit we purchase from carries conditions
 *   R4  everything purchased is settled — the base verdict stands. This means
 *       EVERY purchased unit is `compliant` (empty purchase counts as settled
 *       too); a single unsettled purchased unit (`unknown`, `pending`, or
 *       `null`) is NOT settled, however many compliant siblings sit beside
 *       it — one compliant unit must never mask another unit still waiting.
 */

/** One statement of the framework, reduced to what the cascade reads. Callers
 *  build this from `StatementFacts` + the statement read (scope_all). */
export interface UnitFacts {
  verdict: Verdict | null
  /** The statement covers the whole resolved pool (server `scope_all`). */
  scopeAll: boolean
  /** The statement's PERSISTED article subset (public numbers). Empty +
   *  !scopeAll means "kein Bezug" — a real answer, NOT an unresolved scope.
   *  A scopeAll statement's own `articleNumbers` is ALWAYS empty by contract
   *  (`StatementGroupEntry`'s doc: "scopeAll → fans out … at approve
   *  (articleNumbers empty)") — this field alone can never answer "how many
   *  articles", which is exactly why `resolvedArticleNumbers` exists below.
   *  Used only for the purchase-membership test (`inScope`), never for a
   *  count. */
  articleNumbers: readonly string[]
  /** The CONCRETE articles this statement's scope resolves to right now: M2's
   *  resolution for a scopeAll statement, else the same as `articleNumbers`.
   *  Empty on a scopeAll statement means "not resolved yet", not "zero
   *  articles" — R2 reads that distinction to avoid claiming a count it does
   *  not have (see the whole-scope branch below). */
  resolvedArticleNumbers: readonly string[]
  /** Scope references the pipeline could not resolve to articles (M2
   *  `unresolved_refs`) — the "zum Teil aus grauem Tüllenmaterial" case. */
  unresolvedRefs: number
}

/** The framework's Rückfrage state, from GET /declarations/{id}/inquiries:
 *  absent from that read = "none"; "draft" never crosses the wire. */
export type InquiryState = "none" | "sent" | "answered" | "drafted"

export type FrameworkDerivation =
  | {
      rule: "R1"
      sent: boolean
      /** Questions collected for this framework but not sent yet (the
       *  `drafted` inquiry state): the Rückfrage exists, the reviewer just
       *  has to send it, so Anfordern, not Zurückstellen, is the next move. */
      drafted?: boolean
      /** Set on a `drafted` derivation whose statements also carry scope
       *  references M2 could not resolve. The draft short-circuits the
       *  unresolved-scope check below it, so without this flag a collected
       *  question would hide a stop the same framework has without one. */
      unresolvedScope?: boolean
    }
  | {
      rule: "R2"
      /** Unique non-compliant article count, or `null` when at least one
       *  contributing unit is scopeAll with NO resolved article set yet — a
       *  real "we don't know the count" state, never rendered as 0. */
      nonCompliantArticles: number | null
      nonCompliantUnits: number
      /** At least one PURCHASED unit is plain compliant alongside the
       *  non-compliant one(s). Only then is the head a mixed picture the
       *  split pill can honestly draw; with nothing compliant in scope the
       *  head is simply non-compliant (a lone all-articles non-compliant
       *  statement rendered "Konform | nicht konform" before, claiming a
       *  compliant half that does not exist). */
      anyCompliant: boolean
    }
  | { rule: "R3" }
  | {
      rule: "R4"
      verdict: Verdict
      /** Adverse units exist but none of them touches our purchasing — the
       *  prototype's "effektiv konform" hint. */
      effectivelyCompliant: boolean
      /** The complement, on the SAME "verdict: compliant" branch:
       *  every unit we purchase reads "compliant" AND no adverse unit
       *  exists outside our purchase either — there is no exception to
       *  except at all, not even elsewhere. Mutually exclusive with
       *  `effectivelyCompliant` (exactly one of the two can hold whenever
       *  `verdict === "compliant"`) — never both, never neither, on a
       *  framework with at least one purchased unit. False on the
       *  vacuous empty-purchase case (`purchased.length === 0`, i.e.
       *  nothing named — see `.every()`'s own comment above): there is
       *  nothing to call "all OK" when nothing was bought at all, and
       *  false unconditionally on the unresolved (pending/unknown)
       *  verdict branch, where neither hint applies.
       *
       *  Optional so every existing `FrameworkDerivation` literal built
       *  before this field existed (fixtures across this feature's other
       *  test files, out of this change's scope) keeps compiling without
       *  edits — `undefined` reads exactly like `false` at every call
       *  site. */
      allOkInB?: boolean
    }

/** A unit is "in unserem Bezug" when it covers the whole pool or names at least
 *  one purchased article. An explicit empty subset opted the unit out. */
const inScope = (u: UnitFacts): boolean =>
  u.scopeAll || u.articleNumbers.length > 0

const adverse = (v: Verdict | null): boolean =>
  v === "compliant_with_conditions" || v === "non_compliant"

/**
 * Derive the framework's display verdict. Returns null for a framework with no
 * statements at all — there is nothing to say a verdict about.
 */
export function deriveFrameworkVerdict(
  units: readonly UnitFacts[],
  inquiryState: InquiryState = "none",
): FrameworkDerivation | null {
  if (units.length === 0) return null

  // R1 — an open Rückfrage outranks everything: the framework is waiting on the
  // supplier, whatever else its statements say. An unresolved scope without a
  // sent Rückfrage is the same stop one step earlier ("Rückfrage nötig").
  const unresolvedScope = units.some((u) => u.unresolvedRefs > 0)
  if (inquiryState === "sent") return { rule: "R1", sent: true }
  if (inquiryState === "drafted")
    return unresolvedScope
      ? { rule: "R1", sent: false, drafted: true, unresolvedScope: true }
      : { rule: "R1", sent: false, drafted: true }
  if (unresolvedScope) return { rule: "R1", sent: false }

  const purchased = units.filter(inScope)

  const nonCompliant = purchased.filter((u) => u.verdict === "non_compliant")
  if (nonCompliant.length > 0) {
    // A scopeAll unit with no resolution yet has NO known article set — its
    // own `articleNumbers` is contractually empty (never "0 articles"), so
    // summing resolved counts would silently under-report. Whole-scope wins:
    // one such unit and the total count is unknown, not partially known.
    const wholeScopeUnresolved = nonCompliant.some(
      (u) => u.scopeAll && u.resolvedArticleNumbers.length === 0,
    )
    const articles = new Set<string>()
    for (const u of nonCompliant)
      u.resolvedArticleNumbers.forEach((a) => articles.add(a))
    return {
      rule: "R2",
      nonCompliantArticles: wholeScopeUnresolved ? null : articles.size,
      nonCompliantUnits: nonCompliant.length,
      anyCompliant: purchased.some((u) => u.verdict === "compliant"),
    }
  }

  if (purchased.some((u) => u.verdict === "compliant_with_conditions"))
    return { rule: "R3" }

  // R4 — nothing purchased is adverse. "Effektiv konform" says WHY the head is
  // green although the document lists exceptions: none of them is ours. It
  // holds with or without a purchased compliant unit alongside.
  const adverseElsewhere = units.some((u) => !inScope(u) && adverse(u.verdict))
  // "Settled" means EVERY purchased unit reads compliant — never "at least
  // one does". A compliant unit must not mask an unsettled sibling
  // (unknown/pending/null): that sibling's own statement is still open, and
  // an early "compliant" here would have `derivePrimaryAction` lead with
  // "accept" while the (correct) worklist gate still blocks. `.every()` on an
  // empty `purchased` is vacuously true, so "nothing purchased at all" keeps
  // deriving `compliant` exactly as before.
  // The two reassurance hints exist ONLY while the document lists exceptions
  // outside the purchase (the prototype gates both on `hasExc`): a framework
  // with nothing adverse anywhere is plainly compliant and needs no
  // reassurance. They split on whether ANY purchased unit exists — the
  // prototype's `noneMine` ("Keine der ausgenommenen Artikel wird bezogen")
  // vs `allOkInB` ("Alle bezogenen Aussagen sind konform · die Ausnahmen
  // betreffen deinen Bezug nicht") — so exactly one fires under
  // `adverseElsewhere`, never both, never either without it.
  // A statement whose OWN verdict is unassessed (pending/unknown/null) is a
  // real open item WHATEVER its purchase scope: unlike an adverse verdict
  // outside the purchase (masked above via `adverseElsewhere`, "not ours"),
  // "the supplier said nothing about this yet" is not something scope can
  // excuse: nobody has given it an answer. Reading only `purchased` here let
  // a framework with an out-of-scope ("kein Bezug") pending/unknown/null
  // statement, and NOTHING purchased at all, derive "compliant" from
  // `[].every()`'s vacuous truth, the same masking `.every(compliant)`
  // already guards against for a PURCHASED unsettled sibling (the tests
  // above), just not for one sitting outside the purchase (live test L9,
  // wave 5: the footer led with "Freigeben" while "Noch 1 offener Punkt"
  // still showed for exactly this statement).
  const unsettled = units.filter(
    (u) => u.verdict !== "compliant" && !adverse(u.verdict),
  )
  if (unsettled.length === 0)
    return {
      rule: "R4",
      verdict: "compliant",
      effectivelyCompliant: adverseElsewhere && purchased.length === 0,
      allOkInB: adverseElsewhere && purchased.length > 0,
    }

  // At least one unit (purchased or not) is unsettled (non_compliant and
  // compliant_with_conditions already returned or excluded above, so only
  // pending/unknown/null remain here). `unknown` dominates `pending` — "we
  // could not read it" is the stronger reason to request a new document than
  // "not assessed"; a `null` (unassessed) verdict is treated like `pending`.
  const pool = unsettled.map((u) => u.verdict)
  const verdict: Verdict = pool.includes("unknown") ? "unknown" : "pending"
  return { rule: "R4", verdict, effectivelyCompliant: false, allOkInB: false }
}

/** Why R1 blocks the per-Verordnung conform gesture, one copy per reason:
 *  `inquiry-sent` waits on the supplier's reply, `scope-unresolved` waits on
 *  the reviewer (nothing was sent, so there is no reply to wait for). */
export type ConformBlockReason = "inquiry-sent" | "scope-unresolved"

/**
 * Why R1 blocks the per-Verordnung conform gesture, or `null` when it does
 * not. A sent Rückfrage that names articles does not block: approve holds
 * exactly those articles back until the supplier's reply declaration writes
 * them, so the rest of the Verordnung can be checked and released now. A
 * drafted Rückfrage does not block on its own either (TUR-1581): nothing was
 * sent, so there is no reply to wait for. A sent Rückfrage without articles
 * still blocks, and so does an unresolved scope, with or without a draft.
 */
export function conformBlockReason(
  derivation: FrameworkDerivation | null,
  inquiryArticleCount: number,
): ConformBlockReason | null {
  if (derivation?.rule !== "R1") return null
  if (derivation.sent) return inquiryArticleCount > 0 ? null : "inquiry-sent"
  if (derivation.drafted && !derivation.unresolvedScope) return null
  return "scope-unresolved"
}

/** Does R1 block the per-Verordnung conform gesture? See `conformBlockReason`. */
export function inquiryBlocksConform(
  derivation: FrameworkDerivation | null,
  inquiryArticleCount: number,
): boolean {
  return conformBlockReason(derivation, inquiryArticleCount) !== null
}

/**
 * The declaration-level primary action, derived from every framework's
 * derivation — the prototype's `claimPrimary`. The footer leads with it; the
 * other actions stay offered beside it (the journey never dead-ends).
 */
export type PrimaryAction = "accept" | "request" | "defer"

export function derivePrimaryAction(
  derivations: readonly (FrameworkDerivation | null)[],
): PrimaryAction {
  const present = derivations.filter(
    (d): d is FrameworkDerivation => d !== null,
  )
  // Collected questions waiting to go out win first, even alongside a
  // framework parked on a SENT Rückfrage elsewhere: sending the one
  // declaration-scoped mail is the next step (Ferdi, live test wave 5:
  // Anfordern is the one primary, Freigeben is not). Review wave int (F3):
  // this check used to run AFTER the plain "any R1" defer below, so a sent
  // Rückfrage in one framework forced "defer" for the whole declaration even
  // while another framework held a ready-to-send draft, hiding the one
  // actionable step behind a framework that had nothing left to do but wait.
  if (present.some((d) => d.rule === "R1" && d.drafted)) return "request"
  // A framework waiting on (or needing) a Rückfrage, with no drafted
  // questions anywhere to send instead: the declaration is parked, not
  // requestable. Deferring keeps the thread with the supplier the answer.
  if (present.some((d) => d.rule === "R1" && !d.drafted)) return "defer"
  if (present.some((d) => d.rule === "R2")) return "request"
  if (
    present.some(
      (d) =>
        d.rule === "R4" && (d.verdict === "unknown" || d.verdict === "pending"),
    )
  )
    return "request"
  return "accept"
}
